/**
 * Zustand store for game state.
 * Manages player ship, remote ships, world setup, connection status, and input.
 */

import { create } from 'zustand';
import type { ShipState, WorldSetup, InputFrame, ScenerySetup } from '@warmancer/shared';
import type { LiveFrameMetrics } from '../game/systems/FrameTimingBuffer';
import type * as THREE from 'three';

export type ConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'reconnecting';

/** If pending inputs are this far ahead of ackedSeq, they're stale from a previous session. */
const MAX_PENDING_SEQ_GAP = 128;

export interface AsteroidVisual {
  readonly x: number;
  readonly y: number;
  readonly radius: number;
  readonly vx: number;
  readonly vy: number;
}

export interface RemotePlayer {
  readonly entityId: number;
  readonly username: string;
  readonly state: ShipState;
  readonly prevState: ShipState | null;
  readonly stateTime: number;
}

interface GameState {
  // Connection
  connectionStatus: ConnectionStatus;
  localEntityId: number | null;

  // World
  worldSetup: WorldSetup | null;
  scenerySetup: ScenerySetup | null;
  serverTick: number;
  moonAngle: number;
  asteroidPositions: AsteroidVisual[];

  // Local player
  localShip: ShipState | null;
  serverShip: ShipState | null;
  serverShipTick: number;
  lastAckedInput: number;

  // Remote players
  remotePlayers: Map<number, RemotePlayer>;

  // Input tracking
  inputSequence: number;
  pendingInputs: InputFrame[];

  // Reconnection detection
  worldSetupEpoch: number;
  lastSnapshotReceivedAt: number;

  // Remote ship predicted positions (from Rapier physics world)
  remotePredictedPositions: Map<number, { x: number; y: number; vx: number; vy: number; rotation: number }>;

  // Sub-tick interpolation alpha: fraction of tick elapsed since last physics step.
  // Used by render components for velocity-based extrapolation (0 = at physics pos, ~1 = almost next tick).
  renderAlpha: number;

  // Diagnostics
  desyncDistance: number;
  clientTick: number;
  lastSnapshotTick: number;
  liveFrameMetrics: LiveFrameMetrics;

  // Debug
  cameraFollow: boolean;

  // Camera reference (for mouse-to-world projection)
  cameraRef: THREE.PerspectiveCamera | null;

  // Actions
  setConnectionStatus: (status: ConnectionStatus) => void;
  setLocalEntityId: (id: number) => void;
  setWorldSetup: (setup: WorldSetup) => void;
  setScenerySetup: (setup: ScenerySetup) => void;
  updateFromSnapshot: (
    serverTick: number,
    lastAckedInput: number,
    entities: ReadonlyArray<ShipState>,
    moonAngle: number,
  ) => void;
  addRemotePlayer: (entityId: number, username: string) => void;
  removeRemotePlayer: (entityId: number) => void;
  setMoonAngle: (angle: number) => void;
  setAsteroidPositions: (positions: AsteroidVisual[]) => void;
  setLocalShip: (ship: ShipState) => void;
  clearServerShip: () => void;
  addPendingInput: (input: InputFrame) => void;
  clearAckedInputs: (ackedSeq: number) => void;
  clearPendingInputs: () => void;
  nextInputSequence: () => number;
  setRemotePredictedPositions: (positions: Map<number, { x: number; y: number; vx: number; vy: number; rotation: number }>) => void;
  setRenderAlpha: (alpha: number) => void;
  setDesyncDistance: (distance: number) => void;
  setClientTick: (tick: number) => void;
  setLastSnapshotTick: (tick: number) => void;
  setLiveFrameMetrics: (metrics: LiveFrameMetrics) => void;
  toggleCameraFollow: () => void;
  setCameraRef: (camera: THREE.PerspectiveCamera) => void;
  reset: () => void;
}

const initialState = {
  connectionStatus: 'disconnected' as ConnectionStatus,
  localEntityId: null as number | null,
  worldSetup: null as WorldSetup | null,
  scenerySetup: null as ScenerySetup | null,
  serverTick: 0,
  moonAngle: 0,
  asteroidPositions: [] as AsteroidVisual[],
  localShip: null as ShipState | null,
  serverShip: null as ShipState | null,
  serverShipTick: 0,
  lastAckedInput: 0,
  remotePlayers: new Map<number, RemotePlayer>(),
  inputSequence: 0,
  pendingInputs: [] as InputFrame[],
  worldSetupEpoch: 0,
  lastSnapshotReceivedAt: 0,
  remotePredictedPositions: new Map<number, { x: number; y: number; vx: number; vy: number; rotation: number }>(),
  renderAlpha: 0,
  desyncDistance: 0,
  clientTick: 0,
  lastSnapshotTick: 0,
  liveFrameMetrics: { frameTimeMs: 0, ticksLastFrame: 0, stutterCount: 0 } as LiveFrameMetrics,
  cameraFollow: true,
  cameraRef: null as THREE.PerspectiveCamera | null,
};

export const useGameStore = create<GameState>()((set, get) => ({
  ...initialState,

  setConnectionStatus: (status) => {
    set({ connectionStatus: status });
  },

  setLocalEntityId: (id) => {
    set({ localEntityId: id });
  },

  setWorldSetup: (setup) => {
    set({ worldSetup: setup, worldSetupEpoch: get().worldSetupEpoch + 1 });
  },

  setScenerySetup: (setup) => {
    set({ scenerySetup: setup });
  },

  setMoonAngle: (angle) => {
    set({ moonAngle: angle });
  },

  setAsteroidPositions: (positions) => {
    set({ asteroidPositions: positions });
  },

  updateFromSnapshot: (serverTick, lastAckedInput, entities, _moonAngle) => {
    const state = get();
    const now = performance.now();

    const newRemotePlayers = new Map(state.remotePlayers);
    let newServerShip = state.serverShip;
    let newLocalShip = state.localShip;

    for (const entity of entities) {
      if (entity.entityId === state.localEntityId) {
        // Local player - store server state for reconciliation.
        // Do NOT overwrite localShip — that causes visual jitter between
        // snapshot arrival and reconciliation on the next game tick.
        newServerShip = entity;
        // Bootstrap: if localShip is null (first snapshot after connect),
        // initialize it directly so the game loop can start.
        if (state.localShip === null) {
          newLocalShip = entity;
        }
      } else {
        // Remote player - update with interpolation data
        const existing = newRemotePlayers.get(entity.entityId);
        newRemotePlayers.set(entity.entityId, {
          entityId: entity.entityId,
          username: existing?.username ?? `Player-${entity.entityId}`,
          state: entity,
          prevState: existing?.state ?? null,
          stateTime: now,
        });
      }
    }

    // moonAngle is intentionally NOT set here. The game loop (Game.tsx) is the
    // sole writer for visual moonAngle via setMoonAngle(getExtrapolatedMoonAngle()).
    // Writing it here would cause a dual-writer race: this 20Hz snapshot overwrites
    // the 60Hz extrapolated value, causing the moon to teleport back and forth.
    set({
      serverTick,
      lastAckedInput,
      localShip: newLocalShip,
      serverShip: newServerShip,
      serverShipTick: serverTick,
      remotePlayers: newRemotePlayers,
      lastSnapshotReceivedAt: performance.now(),
    });
  },

  addRemotePlayer: (entityId, username) => {
    const newRemotePlayers = new Map(get().remotePlayers);
    const existing = newRemotePlayers.get(entityId);
    if (existing) {
      newRemotePlayers.set(entityId, { ...existing, username });
    } else {
      newRemotePlayers.set(entityId, {
        entityId,
        username,
        state: {
          entityId,
          x: 0,
          y: 0,
          vx: 0,
          vy: 0,
          rotation: 0,
          health: 100,
          battery: 100,
          alive: true,
          afterburner: false,
          thrusting: false,
          braking: false,
          strafingLeft: false,
          strafingRight: false,
          firing: false,
        },
        prevState: null,
        stateTime: performance.now(),
      });
    }
    set({ remotePlayers: newRemotePlayers });
  },

  removeRemotePlayer: (entityId) => {
    const newRemotePlayers = new Map(get().remotePlayers);
    newRemotePlayers.delete(entityId);
    set({ remotePlayers: newRemotePlayers });
  },

  setLocalShip: (ship) => {
    set({ localShip: ship });
  },

  clearServerShip: () => {
    set({ serverShip: null });
  },

  addPendingInput: (input) => {
    set({ pendingInputs: [...get().pendingInputs, input] });
  },

  clearAckedInputs: (ackedSeq) => {
    const pending = get().pendingInputs;
    // Detect stale inputs from a previous session: if the oldest pending input
    // has a sequence far ahead of the acked sequence, the server restarted and
    // these inputs will never be acknowledged. Clear everything.
    if (pending.length > 0 && pending[0].sequence > ackedSeq + MAX_PENDING_SEQ_GAP) {
      set({ pendingInputs: [], inputSequence: 0 });
      return;
    }
    set({
      pendingInputs: pending.filter((i) => i.sequence > ackedSeq),
    });
  },

  clearPendingInputs: () => {
    set({ pendingInputs: [], inputSequence: 0 });
  },

  nextInputSequence: () => {
    const seq = get().inputSequence + 1;
    set({ inputSequence: seq });
    return seq;
  },

  setRemotePredictedPositions: (positions) => {
    set({ remotePredictedPositions: positions });
  },

  setRenderAlpha: (alpha) => {
    set({ renderAlpha: alpha });
  },

  setDesyncDistance: (distance) => {
    set({ desyncDistance: distance });
  },

  setClientTick: (tick) => {
    set({ clientTick: tick });
  },

  setLastSnapshotTick: (tick) => {
    set({ lastSnapshotTick: tick });
  },

  setLiveFrameMetrics: (metrics) => {
    set({ liveFrameMetrics: metrics });
  },

  toggleCameraFollow: () => {
    set({ cameraFollow: !get().cameraFollow });
  },

  setCameraRef: (camera) => {
    set({ cameraRef: camera });
  },

  reset: () => {
    set({
      ...initialState,
      serverShip: null,
      serverShipTick: 0,
      remotePlayers: new Map(),
      remotePredictedPositions: new Map(),
      renderAlpha: 0,
      pendingInputs: [],
      asteroidPositions: [],
      scenerySetup: null,
      worldSetupEpoch: 0,
      lastSnapshotReceivedAt: 0,
      desyncDistance: 0,
      clientTick: 0,
      lastSnapshotTick: 0,
      liveFrameMetrics: { frameTimeMs: 0, ticksLastFrame: 0, stutterCount: 0 },
      cameraRef: null,
    });
  },
}));
